/**
 * Add primary key and foreign key info to a fields resultset from a relationship resultset
 * More specifically, appends these properties to a field resultset:
 * - key_type   Can be "Primary Key", "Foreign Key" or undefined. Intended for usage in a table column to display icons
 * - key_list   If key_type = "Primary Key" and the primary key is a composite key (consists of multiple columns),
 *              a string on the format "Primary Key (column1, column2, ..., columnN)". WIll otherwise be the same as key_type.
 *              This is intended to be used as tooltip.
 *
 * The returned fields are also ordered for display, depending on the datasource type category:
 * - api  : by ordinal_position, preserving the order fields appear in the OpenAPI schema.
 * - other: grouped Primary Key -> Foreign Key -> rest, stable within each group (so the
 *          backend's field_name ASC order is kept inside a group).
 *
 * @param {object[]}    fields          Resultset from the fields db table
 * @param {object[]}    relationships   Resultset from the relationships db table
 * @returns object[]    fields object[] with appended key_type and key_list properties.
 */
export const updateFieldsWithRelationships = ({ fields, relationships }) => {
  // Turns an array to string, for usage in e.g. tooltips. A field that is no kind of key has nothing
  // to say: without this it described itself as "null", which is what its tooltip then read.
  const keyListToString = (type, list) => {
    if (!type) return undefined;
    let l = list.length > 1 ? ` (${list.join(', ')})` : '';
    return `${type}${l}`;
  };

  let primaryKeyList = [];

  let newFields = fields.map(field => {
    let keyType = null;
    let keyList = [];

    if (field.is_primary_key) {
      keyType = 'Primary Key';
      primaryKeyList.push(field.field_name);
    } else {
      relationships.forEach(relationship => {
        if (
          relationship.fields.find(
            x => x.to_field_id === field.field_id && relationship.relationship_type !== 'virtual'
          )
        ) {
          keyType = 'Primary Key';
          primaryKeyList = relationship.fields.map(x => x.to_field_name);
        } else if (
          relationship.fields.find(
            x => x.from_field_id === field.field_id || x.to_field_id === field.field_id
          )
        ) {
          keyType = 'Foreign Key';
          keyList = relationship.fields.map(x => x.from_field_name);
        }
      });
    }

    return { ...field, key_type: keyType, key_list: keyListToString(keyType, keyList) };
  });

  newFields = newFields.map(field =>
    field.key_type === 'Primary Key'
      ? {
          ...field,
          key_list: keyListToString(field.key_type, primaryKeyList),
        }
      : field
  );

  // API datasets carry a meaningful source order (the order fields appear in the OpenAPI
  // schema); ordinal_position restarts per nesting level, but the table rebuilds the
  // hierarchy by parent_field_id afterwards, so a flat sort still yields correct sibling order.
  if (newFields[0]?.datasource_type_category === 'api') {
    newFields.sort((a, b) => a.ordinal_position - b.ordinal_position);
  } else {
    // Relational datasets have no inherent field order, so group primary key, foreign key
    // and name. Same-type pairs return 0, keeping the backend's field_name ASC within a group.
    newFields.sort((a, b) => {
      if (a.key_type === 'Primary Key' && b.key_type === 'Foreign Key') {
        return -1;
      }
      if (b.key_type === 'Primary Key' && a.key_type === 'Foreign Key') {
        return 1;
      }
      if (a.key_type === null && b.key_type === null) {
        return 0;
      }
      if (a.key_type === null) {
        return 1;
      }
      if (b.key_type === null) {
        return -1;
      }
      return 0;
    });
  }

  // Return fields enriched with key_type and key_list
  return newFields;
};
